/**
 * @module Form
 */

import React, { forwardRef } from 'react';
import { Form as AntForm } from 'antd';
import { Label } from '../../label/label.js';
import { NackleCaption } from '../../NackleCaption/nackle-caption.js';

/**
 * Wraps children components and adds additional props
 * @ignore
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to wrap
 * @param {Object} [props.captionProps] - Props to pass to caption component
 * @param {string} [props.validateStatus] - Validation status
 * @param {string} [props.help] - Help text to display
 * @returns {JSX.Element} Wrapped children with caption
 */
const ChildrenWithProps = ( props ) => {
    const { children, captionProps, validateStatus, help, ...restProps } = props;
    const childrenWithProps = React.Children.map( children, child => {
        const clonedProps = { ...restProps, ...child.props };
        if ( React.isValidElement( child ) ) {
            return React.cloneElement( child, { ...clonedProps } );
        }
        return child;
    } );
    return (
        <>
            { help ? <NackleCaption className="form-item-help" text={ help } state={ validateStatus } { ...captionProps } /> : null }
            { childrenWithProps }
        </>
    );
};

/**
 * Form item component that wraps Ant Design Form.Item
 * @param {Object} props - Component props
 * @param {string} [props.label] - Label text
 * @param {Object} [props.labelProps] - Props to pass to Label component
 * @param {string} [props.extra] - Extra text to display
 * @param {Object} [props.captionProps] - Props to pass to caption components
 * @param {React.ReactNode} props.children - Child components
 * @param {boolean} [props.required] - Whether field is required
 * @param {string} [props.validateStatus] - Validation status
 * @param {boolean} [props.optional] - Whether field is optional
 * @param {Array} [props.rules] - Validation rules
 * @param {string} [props.help] - Help text
 * @param {string} [props.className] - Additional CSS class
 * @param {React.Ref} ref - Forwarded ref
 * @returns {JSX.Element} Form item component
 */
const Item = forwardRef( ( props, ref ) => {
    const { label, labelProps, extra, captionProps, children, required, validateStatus, optional, rules, help, className, ...restProps } = props;
    const labelRequired = required || rules?.some( rule => rule.required );

    return (
        <AntForm.Item
            ref={ ref }
            label={ label ? <Label className={ help ? 'form-item-with-help' : undefined } text={ label } required={ labelRequired } optional={ optional } status={ validateStatus } { ...labelProps } /> : undefined }
            extra={ extra ? <NackleCaption text={ extra } state={ validateStatus } { ...captionProps } /> : undefined }
            colon={ false }
            required={ required }
            validateStatus={ validateStatus }
            rules={ rules }
            className={ help ? 'has-help' : undefined }
            { ...restProps }
        >
            <ChildrenWithProps help={ help } validateStatus={ validateStatus } captionProps={ captionProps } { ...restProps }>{ children }</ChildrenWithProps>
        </AntForm.Item> );
} );


export { Item };
